import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { openErrorNotification, openSuccessNotification } from "helpers/methods";
import { useAppSelector } from "redux/hooks";
import { selectAuthenticated, selectEmail } from "redux/authSlice";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import ChatThread from "./components/ChatThread";
import Composer from "./components/Composer";
import ContractSearchEmptyState from "./components/ContractSearchEmptyState";
import ContractSearchHeader from "./components/ContractSearchHeader";
import PassageViewer from "./components/PassageViewer";
import styles from "./ContractSearch.styles";
import {
  byDocumentId,
  bumpSessionEpoch,
  clearThread,
  currentSessionEpoch,
  errorTurn,
  isBadConversationIdError,
  isConversationId,
  loadConversationId,
  loadThread,
  nextMessageId,
  plainAnswer,
  saveConversationId,
  saveThread,
  toAiTurn,
} from "./ContractSearchHelper";
import {
  fetchAllContractDocuments,
  searchContracts,
} from "./ContractSearchService";
import {
  AiMessage,
  ChatMessage,
  ContractDocument,
  MatchedContract,
  MatchedPassage,
  ViewerState,
} from "./ContractSearchTypes";
import { useCompareEntry } from "./useCompareEntry";

/**
 * Contract Search (`/contracts/search`) — ask one question across every contract
 * the user owns; the AI answers with quoted, page-located passages.
 *
 * Access: the `sign` / `fetch` permission (the same envelopes the sign screens
 * show). Deliberately **not** plan-gated — product decision, 2026-09-16. The entry
 * points into Compare contract versions (the control beside a matched PDF / Word
 * contract) are the one gated part: they look locked without AI Assistant.
 *
 * A dashboard prompt arrives as `history.location.state.ask` and is sent on mount.
 */
const ContractSearchComponent = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const copy = strings.contractSearch;

  useTitle(title.CONTRACT_SEARCH);
  useSetPageHeader(isPhone ? copy.title : undefined);

  const hasFetchPermission = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );
  const userEmail = useAppSelector(selectEmail);
  const authenticated = useAppSelector(selectAuthenticated);
  const threadKey = (userEmail || "anonymous").toLowerCase();
  const {
    lock: compareLock,
    upgradeOpen,
    closeUpgrade,
    openCompare,
  } = useCompareEntry();

  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  // False when the list call failed: the count is unknown, but search itself still works.
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    loadThread(threadKey),
  );
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [focusComposer, setFocusComposer] = useState(false);
  const mounted = useRef(true);
  // The AI's conversation id. A ref, not state: it is never rendered, and `ask` must always read the
  // latest value. Persisted with the thread so leaving and returning continues the conversation.
  const conversationId = useRef<string | null>(loadConversationId(threadKey));
  const setConversation = useCallback(
    (id: string | null) => {
      conversationId.current = id;
      saveConversationId(threadKey, id);
    },
    [threadKey],
  );

  const docsById = useMemo(() => byDocumentId(documents), [documents]);
  // The API's own count, shown exactly as given -- no filtering by visibleInRepository or status.
  const [totalIndexed, setTotalIndexed] = useState(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The documents list drives the indexed count and the row meta. One load per visit.
  useEffect(() => {
    if (!hasFetchPermission) {
      setDocumentsLoading(false);
      return;
    }
    let cancelled = false;
    setDocumentsLoading(true);
    fetchAllContractDocuments()
      .then(({ documents: docs, total }) => {
        if (cancelled) return;
        setDocuments(docs);
        setTotalIndexed(total);
        setDocumentsLoaded(true);
      })
      .catch(() => {
        // The header degrades to "your contracts"; search still works without the list.
        if (cancelled) return;
        setDocumentsLoaded(false);
        openErrorNotification(notifiers.CONTRACT_DOCUMENTS_FAILED);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasFetchPermission]);

  useEffect(() => {
    saveThread(threadKey, messages);
  }, [messages, threadKey]);

  // Logout (any route) while this screen is still mounted: drop the conversation from state and refs
  // too. The store middleware has already cleared sessionStorage and invalidated in-flight requests.
  useEffect(() => {
    if (authenticated) return;
    conversationId.current = null;
    setMessages([]);
    setExpanded({});
    setViewer(null);
    setDraft("");
    setIsSending(false);
  }, [authenticated]);

  const ask = useCallback(
    async (text: string) => {
      const query = text.trim();
      if (!query || isSending) return;

      const userId = nextMessageId();
      const aiId = nextMessageId();
      setMessages((prev) => [
        ...prev,
        { id: userId, role: "user", text: query },
        { id: aiId, role: "ai", query, thinking: true, turn: null },
      ]);
      setDraft("");
      setViewer(null);
      setIsSending(true);
      // Answers that arrive after New search / logout belong to a dead conversation: drop them.
      const epoch = currentSessionEpoch();
      const stale = () => epoch !== currentSessionEpoch();

      try {
        let response;
        try {
          response = await searchContracts(query, conversationId.current);
        } catch (error: any) {
          // 404 with an id = that conversation ended (expired or unknown); 400 naming the id = the id
          // itself is unusable. Either way: drop it and ask the same question once more as a new
          // conversation. If the 404 was "no contracts yet", the retry gets the same 404 and it is
          // shown as the error turn below.
          const conversationRefused =
            error?.statusCode === 404 || isBadConversationIdError(error);
          if (!conversationRefused || !conversationId.current || stale()) throw error;
          setConversation(null);
          response = await searchContracts(query, null);
        }
        if (stale()) return;
        // Saved before the mounted check: leaving the screen mid-request must not lose the first id.
        // A null id after we sent one means memory was unavailable this turn: keep the old id.
        if (isConversationId(response.conversationId)) setConversation(response.conversationId);
        if (!mounted.current) return;
        const turn = toAiTurn(response, docsById);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiId ? { ...m, thinking: false, turn } : m,
          ),
        );
        // Open the first cited contract, as the design does.
        const first = turn.contracts.find((c) => c.used);
        if (first) {
          setExpanded((prev) => ({
            ...prev,
            [`${aiId}:${first.documentId}`]: true,
          }));
        }
      } catch (error: any) {
        if (stale() || !mounted.current) return;
        // A 401 has already been turned into a logout by makeCall; everything else is an error turn.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiId ? { ...m, thinking: false, turn: errorTurn(error) } : m,
          ),
        );
      } finally {
        if (mounted.current && !stale()) setIsSending(false);
      }
    },
    [docsById, isSending, setConversation],
  );

  // A prompt chosen on the dashboard card lands here through router state; send it once.
  const consumedDashboardAsk = useRef(false);
  useEffect(() => {
    const state = history.location.state as { ask?: string } | undefined;
    if (state?.ask && !consumedDashboardAsk.current && hasFetchPermission) {
      consumedDashboardAsk.current = true;
      history.replace({ pathname: history.location.pathname, state: {} });
      ask(state.ask);
    }
  }, [ask, hasFetchPermission]);

  const onRetry = (message: AiMessage) => {
    if (isSending) return;
    // Drop the old answer and its question, then ask again so the thread reads naturally.
    setMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index < 1) return prev;
      return [...prev.slice(0, index - 1), ...prev.slice(index + 1)];
    });
    // Let the removal render first so the new pair appends cleanly.
    setTimeout(() => ask(message.query), 0);
  };

  const onCopy = async (message: AiMessage) => {
    // The clipboard gets the answer without its Markdown markers.
    const text = plainAnswer(message.turn?.answer ?? "");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      openSuccessNotification(notifiers.CONTRACT_ANSWER_COPIED);
    } catch {
      openErrorNotification(notifiers.GENERIC_ERROR);
    }
  };

  const onNewSearch = () => {
    // Invalidate any request still in flight so its answer cannot bring the old id back.
    bumpSessionEpoch();
    setIsSending(false);
    setMessages([]);
    setExpanded({});
    setViewer(null);
    setDraft("");
    clearThread(threadKey);
    // A new search is a new conversation: the next question must not carry the old id.
    setConversation(null);
    // Toggle so a second click (thread already empty) still moves focus into the box.
    setFocusComposer(false);
    requestAnimationFrame(() => setFocusComposer(true));
  };

  const onToggle = (messageId: string, documentId: string) => {
    const key = `${messageId}:${documentId}`;
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onOpenPassage = (passage: MatchedPassage, contract: MatchedContract, passageKey: string) => {
    setViewer({
      documentId: contract.documentId,
      fileName: contract.fileName,
      page: passage.page,
      linesLabel: passage.linesLabel,
      quote: passage.quote,
      sourceRequestId: contract.sourceRequestId,
      passageKey,
    });
  };

  const onCloseViewer = () => {
    const key = viewer?.passageKey;
    setViewer(null);
    // Return focus to the passage that opened the viewer.
    if (key) {
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(
          `[data-passage-key="${key}"]`,
        );
        el?.focus();
      });
    }
  };

  const onOpenPdf = (view: ViewerState) => {
    if (!view.sourceRequestId) return;
    history.push(`${viewpaths.viewRequestViewPath}/${view.sourceRequestId}`);
  };

  if (!hasFetchPermission) {
    return <Unauthorized permissionDenied />;
  }

  // Only a confirmed empty repository disables the box; an unknown count must not block search.
  const composerDisabled = !documentsLoading && documentsLoaded && totalIndexed === 0;

  return (
    <CenteredAuthenticatedLayout>
      <Box sx={styles.page}>
        <ContractSearchHeader
          totalIndexed={totalIndexed}
          countKnown={documentsLoaded}
          documentsLoading={documentsLoading}
          onNewSearch={onNewSearch}
          isPhone={isPhone}
        />

        <Box sx={styles.chatArea}>
          {/* The thread scrolls in the middle; the composer floats at the bottom. */}
          <Box sx={styles.scrollArea}>
            <Box sx={styles.columns}>
              <Box sx={styles.threadColumn}>
                {messages.length === 0 ? (
                  <ContractSearchEmptyState
                    totalIndexed={totalIndexed}
                    countKnown={documentsLoaded}
                    onAsk={ask}
                    disabled={documentsLoading || isSending}
                  />
                ) : (
                  <ChatThread
                    messages={messages}
                    totalIndexed={totalIndexed}
                    isSending={isSending}
                    expanded={expanded}
                    onToggle={onToggle}
                    activePassageKey={viewer?.passageKey ?? null}
                    onOpenPassage={onOpenPassage}
                    onAsk={ask}
                    onCopy={onCopy}
                    onRetry={onRetry}
                    onCompare={openCompare}
                    compareLock={compareLock}
                  />
                )}
              </Box>

            </Box>
          </Box>

          <Box sx={styles.composerWrap}>
            <Box sx={styles.composerColumn}>
              <Composer
                value={draft}
                onChange={setDraft}
                onSend={ask}
                disabled={composerDisabled}
                isSending={isSending}
                autoFocus={focusComposer}
              />
            </Box>
          </Box>
        </Box>

        {/* Centred modal (bottom sheet on phones); it overlays the thread, so nothing shifts. */}
        <PassageViewer
          viewer={viewer}
          onClose={onCloseViewer}
          onOpenPdf={onOpenPdf}
        />

        {/* A locked compare entry explains itself here: the plan has no AI Assistant. */}
        <UpgradeModal
          open={upgradeOpen}
          featureKey={FeatureKeys.AI_ASSISTANT}
          onClose={closeUpgrade}
        />
      </Box>
    </CenteredAuthenticatedLayout>
  );
};

export default ContractSearchComponent;
